import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useProjects, useReport } from '../api/queries';
import type { Finding, FindingStatus, ReportResponse } from '../api/types';
import { GapList, HistoricalCoverage, SourceCoverageMatrix } from '../components/Coverage';
import { EvidenceTallyLine } from '../components/Evidence';
import { findingPath } from '../components/FindingCard';
import { Prose } from '../components/Prose';
import { EmptyState, QueryState } from '../components/States';
import { StatusBadge, StatusLegend } from '../components/StatusBadge';
import { chapterNumber, formatDate, paragraphs, plural, statusMeta } from '../lib/format';

function anchorFor(chapterId: string): string {
  return chapterId.replace(/^chapter:/, '').replace(/[^a-z0-9-]/gi, '-');
}

function StatusSummary({ findings }: { findings: Finding[] }) {
  const counts = (Object.keys(statusMeta) as FindingStatus[]).map((status) => ({
    status,
    count: findings.filter((finding) => finding.status === status).length,
  }));
  const reviewed = counts.find((entry) => entry.status === 'reviewed')?.count ?? 0;
  const flagged = findings.filter((finding) => finding.needsReview).length;
  return (
    <div className="report-status">
      <p className="report-status__lead">
        {findings.length === 0
          ? 'No findings are visible to your account yet.'
          : reviewed === 0
            ? `All ${plural(findings.length, 'finding')} below are proposed. None has been reviewed by a steward yet, so read them as drafts open to challenge.`
            : `${plural(reviewed, 'finding')} of ${String(findings.length)} ${reviewed === 1 ? 'has' : 'have'} been reviewed by a steward. The rest are drafts, disputes or history — each is labelled.`}
      </p>
      <ul className="report-status__counts">
        {counts
          .filter((entry) => entry.count > 0)
          .map((entry) => (
            <li key={entry.status}>
              <StatusBadge status={entry.status} /> <span className="report-status__n">{entry.count}</span>
            </li>
          ))}
        {flagged > 0 && <li className="report-status__flag">{plural(flagged, 'finding')} flagged for review after new material</li>}
      </ul>
    </div>
  );
}

/** The current operating direction, kept distinct from the research that follows it. */
function CurrentDirection() {
  return (
    <aside className="direction" aria-labelledby="direction-title">
      <div className="direction__inner">
        <p className="eyebrow">Current direction · selected September 11, 2026</p>
        <h2 id="direction-title" className="direction__title">
          One project loop, piloted on two projects
        </h2>
        <p>
          Bring a question, learning goal or worthwhile problem; make progress; keep a useful update; get help; and iterate. GitFitCode Hub
          and UpAhead MVP are the two selected pilots, and implementation is underway. This direction builds on the research below; it does
          not change the review status of its six chapters or their proposed findings.
        </p>
        <Link to="/pilots" className="text-link">
          See the selected pilots and how to take part →
        </Link>
      </div>
    </aside>
  );
}

function ChapterFinding({ finding, projectTitle }: { finding: Finding; projectTitle: string | undefined }) {
  return (
    <li className={`chapter-finding chapter-finding--${finding.status}`}>
      <div className="chapter-finding__meta">
        <StatusBadge status={finding.status} needsReview={finding.needsReview} />
        {projectTitle !== undefined && <span className="chapter-finding__project">{projectTitle}</span>}
      </div>
      <h3 className="chapter-finding__title">
        <Link to={findingPath(finding.id)}>{finding.title}</Link>
      </h3>
      <p className="chapter-finding__summary">{finding.summary}</p>
      <p className="chapter-finding__interpretation">
        <span className="chapter-finding__label">Our reading</span> {finding.interpretation}
      </p>
      <EvidenceTallyLine evidence={finding.evidence} />
      <Link className="text-link" to={findingPath(finding.id)}>
        Read the evidence, method and discussion<span className="visually-hidden"> for “{finding.title}”</span> →
      </Link>
    </li>
  );
}

function Chapters({ report, projectTitles }: { report: ReportResponse; projectTitles: Map<string, string> }) {
  return (
    <>
      {report.chapters.map((chapter, index) => {
        const [lede, ...rest] = paragraphs(chapter.introduction);
        return (
          <section key={chapter.id} id={anchorFor(chapter.id)} className="chapter" aria-labelledby={`${anchorFor(chapter.id)}-title`}>
            <header className="chapter__opening">
              <p className="chapter__number" aria-hidden="true">
                {chapterNumber(index)}
              </p>
              <div className="chapter__heading">
                <p className="eyebrow">Chapter {index + 1}</p>
                <h2 className="chapter__title" id={`${anchorFor(chapter.id)}-title`}>
                  {chapter.title}
                </h2>
                {chapter.researchQuestion !== undefined && <p className="chapter__question">{chapter.researchQuestion}</p>}
              </div>
            </header>
            <div className="chapter__intro">
              {lede !== undefined && <p className="chapter__lede">{lede}</p>}
              <Prose text={rest.join('\n\n')} />
            </div>
            {chapter.findings.length === 0 ? (
              <EmptyState title="No findings in this chapter are visible to you">
                <p>They may be restricted to stewards, or none have been proposed yet.</p>
              </EmptyState>
            ) : (
              <ol className="chapter__findings" aria-label={`Findings in chapter ${String(index + 1)}`}>
                {chapter.findings.map((finding) => (
                  <ChapterFinding
                    key={finding.id}
                    finding={finding}
                    projectTitle={finding.projectId === undefined ? undefined : projectTitles.get(finding.projectId)}
                  />
                ))}
              </ol>
            )}
            {chapter.openQuestions.length > 0 && (
              <aside className="open-questions" aria-label={`Open questions for chapter ${String(index + 1)}`}>
                <p className="open-questions__title">Still open</p>
                <ul>
                  {chapter.openQuestions.map((question) => (
                    <li key={question}>{question}</li>
                  ))}
                </ul>
                <Link to="/participate" className="text-link">
                  Know something that answers this? Add context or propose a finding →
                </Link>
              </aside>
            )}
          </section>
        );
      })}
    </>
  );
}

/** Client-side routing does not scroll to a fragment by itself; do it once the chapters exist. */
function useScrollToHash(ready: boolean) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || hash === '') return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target === null) return;
    target.scrollIntoView({ block: 'start' });
    const heading = target.querySelector<HTMLElement>('h2');
    if (heading !== null) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }, [ready, hash]);
}

export function Report() {
  const report = useReport();
  const projects = useProjects();
  const projectTitles = new Map((projects.data?.items ?? []).map((project) => [project.id, project.title]));
  useScrollToHash(report.isSuccess);

  return (
    <main id="main" tabIndex={-1} className="report">
      <QueryState query={report} label="the research report">
        {(data) => {
          const findings = data.chapters.flatMap((chapter) => chapter.findings);
          return (
            <>
              <header className="report-hero">
                <div className="report-hero__inner">
                  <p className="eyebrow">GitFitCode community research · sources to {formatDate(data.cutoff)}</p>
                  <h1 className="report-hero__title">How people learned, helped and built together</h1>
                  <p className="report-hero__lede">
                    Six chapters drawn from the community&apos;s own records — Discord conversations, Notion
                    retrospectives, repositories and the original maps. Each finding shows what supports it, what
                    complicates it and what is still unknown. You can challenge any of it.
                  </p>
                  <StatusSummary findings={findings} />
                </div>
              </header>

              <CurrentDirection />

              <div className="report-body">
                <nav className="report-toc" aria-label="Report contents">
                  <p className="report-toc__title">Contents</p>
                  <ol>
                    {data.chapters.map((chapter, index) => (
                      <li key={chapter.id}>
                        <a href={`#${anchorFor(chapter.id)}`}>
                          <span className="report-toc__n">{chapterNumber(index)}</span>
                          <span>{chapter.title}</span>
                        </a>
                      </li>
                    ))}
                    <li>
                      <a href="#sources-and-coverage">
                        <span className="report-toc__n">—</span>
                        <span>Sources, coverage and gaps</span>
                      </a>
                    </li>
                  </ol>
                </nav>

                <div className="report-chapters">
                  <section className="reading-guide" aria-labelledby="reading-guide-title">
                    <h2 id="reading-guide-title" className="reading-guide__title">
                      How to read this report
                    </h2>
                    <p>
                      AI helped propose these findings from the archive; counts come from reproducible queries; a steward
                      publishes a finding only after reviewing it with a recorded reason. A posting pattern is not
                      retention, a task snapshot is not a delivery date, and a proposal is not an agreement.
                    </p>
                    <StatusLegend />
                  </section>

                  {data.chapters.length === 0 ? (
                    <EmptyState title="The report has no chapters yet">
                      <p>The research seed has not been imported for your account.</p>
                    </EmptyState>
                  ) : (
                    <Chapters report={data} projectTitles={projectTitles} />
                  )}

                  <section id="sources-and-coverage" className="chapter chapter--appendix" aria-labelledby="coverage-title">
                    <header className="chapter__opening">
                      <p className="chapter__number" aria-hidden="true">
                        ∗
                      </p>
                      <div className="chapter__heading">
                        <p className="eyebrow">Appendix</p>
                        <h2 className="chapter__title" id="coverage-title">
                          Sources, coverage and gaps
                        </h2>
                        <p className="chapter__question">What does this report rest on, and what is missing?</p>
                      </div>
                    </header>
                    <div className="appendix-grid">
                      <div>
                        <h3 className="section-title">Dated archive reports</h3>
                        <p className="muted">
                          These are dated acquisition and sampling reports. They are research coverage, not measures of
                          membership, engagement or impact.
                        </p>
                        <HistoricalCoverage coverage={data.coverage} />
                      </div>
                      <div>
                        <h3 className="section-title">Imported into this hub</h3>
                        <SourceCoverageMatrix coverage={data.coverage} />
                        <h3 className="section-title">Known gaps</h3>
                        <GapList coverage={data.coverage} />
                        {(data.coverage.limitations ?? []).length > 0 && (
                          <>
                            <h3 className="section-title">Reading limits</h3>
                            <ul className="point-list point-list--limits">
                              {(data.coverage.limitations ?? []).map((limit) => (
                                <li key={limit}>{limit}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </>
          );
        }}
      </QueryState>
    </main>
  );
}
